import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, ChartLineUp, Compass, MagnifyingGlassPlus, Calculator, ArrowLeft, Trash, Clock,
} from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';
import EventModeBanner from '@/components/EventModeBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_META = {
  'geo-checkup':      { icon: Sparkle,             label_tr: 'AI Görünürlük',    label_en: 'AI Visibility',   accent: 'text-[#3CD9B8]' },
  'competitor-radar': { icon: Compass,             label_tr: 'Rakip Radar',      label_en: 'Competitor Radar',accent: 'text-[#0F1B36]' },
  'report-analytics': { icon: ChartLineUp,         label_tr: 'Rapor Analitik',   label_en: 'Report Analytics',accent: 'text-[#D97706]' },
  'pricing-engine':   { icon: Calculator,          label_tr: 'Fiyatlama Motoru', label_en: 'Pricing Engine',  accent: 'text-[#16A34A]' },
  'store-audit':      { icon: MagnifyingGlassPlus, label_tr: 'Mağaza Denetimi',  label_en: 'Store Audit',     accent: 'text-[#7C3AED]' },
};

const formatDate = (iso, language) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) { return iso; }
};

export default function ToolHistoryPage() {
  const { t, language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/tools/history`, { headers: getAuthHeaders() })
      .then((r) => setRuns(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Geçmiş yüklenemedi'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRun = async (run) => {
    setSelected(run);
    setDetail(null);
    try {
      const r = await axios.get(`${API_URL}/tools/history/${run.id}`, { headers: getAuthHeaders() });
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Detay yüklenemedi');
    }
  };

  const deleteRun = async (run) => {
    if (!window.confirm(language === 'tr' ? 'Bu araç kaydını silmek istediğinize emin misiniz?' : 'Delete this tool run?')) return;
    try {
      await axios.delete(`${API_URL}/tools/history/${run.id}`, { headers: getAuthHeaders() });
      setRuns((prev) => prev.filter((r) => r.id !== run.id));
      if (selected?.id === run.id) { setSelected(null); setDetail(null); }
      toast.success(language === 'tr' ? 'Silindi' : 'Deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silme başarısız');
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <EventModeBanner />
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B]" data-testid="back-to-dashboard">
              <ArrowLeft size={22} />
            </Link>
            <Logo variant="light" size="md" />
          </div>
          <Link to="/araclar" className="btn-secondary text-sm" data-testid="new-tool-run">
            {language === 'tr' ? 'Yeni Analiz →' : 'New Analysis →'}
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="label-text text-[#0F1B36] mb-2">
            {language === 'tr' ? 'ARAÇ GEÇMİŞİM' : 'MY TOOL HISTORY'}
          </p>
          <h1 className="heading-2 text-[#09090B]" data-testid="tool-history-title">
            {language === 'tr' ? 'Geçmiş Araç Analizleriniz' : 'Your Past Tool Analyses'}
          </h1>
          <p className="text-[#52525B] mt-2 max-w-2xl">
            {language === 'tr'
              ? 'Ücretsiz araçlar üzerinden yaptığınız tüm analizler burada saklanır. İstediğiniz zaman tekrar görüntüleyip PDF olarak indirebilirsiniz.'
              : 'Every analysis you run with GrowthRIQ tools is stored here. View or export them any time.'}
          </p>
        </div>

        {loading ? (
          <div className="bg-white border border-[#E4E4E7] p-12 text-center text-[#52525B]" data-testid="tool-history-loading">
            {language === 'tr' ? 'Yükleniyor…' : 'Loading…'}
          </div>
        ) : runs.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] p-16 text-center" data-testid="tool-history-empty">
            <Clock size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="heading-4 text-[#09090B] mb-2">
              {language === 'tr' ? 'Henüz bir araç çalıştırmadınız' : 'No tool runs yet'}
            </p>
            <p className="text-[#52525B] mb-6">
              {language === 'tr'
                ? 'AI görünürlük ölçün, rakip analizi yapın, marketplace verinizi yorumlayın.'
                : 'Measure AI visibility, analyze competitors, interpret marketplace data.'}
            </p>
            <Link to="/araclar" className="btn-primary inline-flex items-center gap-2" data-testid="empty-cta-tools">
              {language === 'tr' ? 'Araçları Keşfet' : 'Explore Tools'} →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column: run list */}
            <ul className="lg:col-span-1 space-y-3" data-testid="tool-history-list">
              {runs.map((r) => {
                const meta = TOOL_META[r.tool_slug] || { icon: Sparkle, label_tr: r.tool_slug, label_en: r.tool_slug, accent: 'text-[#0F1B36]' };
                const Icon = meta.icon;
                const isActive = selected?.id === r.id;
                return (
                  <li key={r.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => openRun(r)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRun(r); } }}
                      className={`w-full text-left bg-white border p-4 hover:border-[#0F1B36] transition-colors group cursor-pointer ${isActive ? 'border-[#0F1B36] shadow-sm' : 'border-[#E4E4E7]'}`}
                      data-testid={`tool-run-${r.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon size={22} weight="duotone" className={meta.accent} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-widest text-[#71717A] mb-1">
                            {language === 'tr' ? meta.label_tr : meta.label_en}
                          </p>
                          <p className="font-medium text-[#09090B] truncate group-hover:text-[#0F1B36]">
                            {r.title || (language === 'tr' ? 'Adsız analiz' : 'Untitled run')}
                          </p>
                          <p className="text-xs text-[#71717A] mt-1">{formatDate(r.created_at, language)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteRun(r); }}
                          className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-[#E60000] transition-opacity"
                          data-testid={`tool-run-delete-${r.id}`}
                          title={language === 'tr' ? 'Sil' : 'Delete'}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Right column: detail viewer */}
            <div className="lg:col-span-2">
              {selected ? (
                <div className="bg-white border border-[#E4E4E7] p-6" data-testid="tool-run-detail">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E4E4E7]">
                    <div>
                      <p className="label-text text-[#0F1B36]">
                        {(TOOL_META[selected.tool_slug]?.[language === 'tr' ? 'label_tr' : 'label_en']) || selected.tool_slug}
                      </p>
                      <h2 className="heading-4 text-[#09090B]">{selected.title || '—'}</h2>
                      <p className="text-xs text-[#71717A] mt-1">{formatDate(selected.created_at, language)}</p>
                    </div>
                  </div>
                  {!detail ? (
                    <p className="text-[#52525B]">{language === 'tr' ? 'Detay yükleniyor…' : 'Loading detail…'}</p>
                  ) : (
                    <>
                      {detail.inputs && (
                        <div className="mb-4">
                          <p className="font-medium text-[#09090B] mb-2">{language === 'tr' ? 'Girdi' : 'Inputs'}</p>
                          <pre className="text-xs bg-[#F4F4F5] p-3 overflow-x-auto max-h-40 whitespace-pre-wrap">
                            {JSON.stringify(detail.inputs, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#09090B] mb-2">{language === 'tr' ? 'AI Analiz Sonucu' : 'AI Output'}</p>
                        <pre className="text-xs bg-[#0F1B36] text-white p-4 overflow-x-auto max-h-[500px] whitespace-pre-wrap">
                          {JSON.stringify(detail.output, null, 2)}
                        </pre>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-dashed border-[#E4E4E7] p-16 text-center text-[#52525B]" data-testid="tool-detail-placeholder">
                  {language === 'tr' ? 'Detayı görmek için sol taraftan bir kayıt seçin.' : 'Select a run from the left to view details.'}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
